/**
 * Flag-key registry — public barrel.
 *
 * The flag KEYS and per-flag classification are GENERATED from PostHog (the
 * single source of truth) into `registry.generated.ts` via `yarn flags
 * generate`. Only flags tagged `tribe-neo` are included; classification comes
 * from the `experiment` / `static` tag. Do not hand-edit the generated file —
 * change the flag or its tags in PostHog and re-run the generator.
 *
 * This barrel re-exports the generated registry so every importer of
 * `@bettermode/flags/registry` (the provider, `keys.ts`, tests) keeps resolving
 * unchanged. It is pure data + typed constants — no OpenFeature/PostHog SDK
 * imports — so it respects the `@bettermode/flags` charter. The PostHog provider
 * that consumes this metadata lives in `packages/web/src/server/flags`.
 */
import type { FlagMetadata } from './registry-types.js'
import { FLAG_METADATA, type FlagKey } from './registry.generated.js'

export * from './registry.generated.js'
// Hand-written types. They used to be emitted as template text from the generator,
// so `tsc` never checked them and a shape change meant editing TypeScript inside a
// string. Re-exported here so every existing importer of
// `@bettermode/flags/registry` keeps resolving `FlagMetadata` /
// `FlagClassification` unchanged.
export * from './registry-types.js'

/**
 * Look up per-flag metadata. Returns `undefined` for keys not in the registry so
 * callers can fall back to defaults without throwing. Lives here rather than in
 * the generated file so it is type-checked instead of being template text.
 */
export const getFlagMetadata = (key: string): FlagMetadata | undefined =>
  FLAG_METADATA[key as FlagKey]

/**
 * Every registered flag's metadata as a list.
 *
 * Typed explicitly, and the ONLY way infra should iterate the registry. An EMPTY
 * registry is a valid state — no flag carries the tag yet, or every flag was
 * untagged — and then `FlagKey` is `never`. `Record<never, T>` has no properties,
 * so `Object.values(FLAG_METADATA)` infers `unknown[]` and every consumer of the
 * list becomes a type error. Going through here keeps the empty case compiling.
 */
export const allFlagMetadata = (): FlagMetadata[] =>
  Object.values(FLAG_METADATA) as FlagMetadata[]
